import {
  AssessmentMethod,
  AssessmentMethodType,
  AssessmentWithEntries,
  GroupMember,
  MarkbookAssessmentEntry,
} from '@schooly/api';
import { updateArrayItem, updateAtPath } from '@schooly/components/data-grid';

import { MARKBOOK_PAGE_SIZE } from '../MarkbookPreview/useAssessmentsMarkbookForGroups';
import {
  MarkbookAssessmentEntriesMap,
  MarkbookCategoryWithStudents,
  MarkbookForGroupRow,
  MarkbookForGroupsCategoryRow,
  MarkbookForGroupsLoadingNextPageRow,
  MarkbookForGroupsLoadMoreRow,
  MarkbookForGroupsRow,
  MarkbookForGroupsRowType,
  UpdateAssessmentEntryParams,
  UpdateMarkbookForGroupsAssessmentEntryParams,
} from './types';

export function getMarkbookForGroupAssessmentParams(
  relationId: string,
  method: AssessmentMethod,
  assessment?: AssessmentWithEntries,
) {
  const students = assessment?.groups[0]?.students ?? [];
  const relation = students.find((item) => item.relation_id === relationId);

  if (!method || !assessment || !relation) {
    return null;
  }

  const entries = relation.entries?.filter(
    (item) => item.method.id === method.id && item.method.type === method.method_type,
  );

  return { entries };
}

export function hasMarkbookForGroupAssessmentEntry(
  relationId: string,
  method: AssessmentMethod,
  assessment?: AssessmentWithEntries,
): boolean {
  return !!getMarkbookForGroupAssessmentParams(relationId, method, assessment);
}

export function updateAssessmentEntry(
  entries: AssessmentWithEntries[] | undefined,
  { studentId, assessmentId, methodId, entry, comments }: UpdateAssessmentEntryParams,
): AssessmentWithEntries[] | undefined {
  if (!entries) return entries;

  return updateAtPath(
    { assessments: entries },
    'assessments',
    (assessments: AssessmentWithEntries[]) =>
      updateArrayItem(
        assessments,
        (assessment) => assessment.id === assessmentId,
        (assessment) => {
          const group = assessment.groups[0];
          if (!group) return assessment;

          return {
            ...assessment,
            groups: [
              {
                ...group,
                students: updateArrayItem(
                  group.students,
                  (student) => student.relation_id === studentId,
                  (student) => {
                    const newEntries = comments ?? (entry ? [entry] : []);

                    if (!student.entries?.length) {
                      return { ...student, entries: newEntries };
                    }

                    const entryExists = student.entries.some((e) => e.method.id === methodId);

                    if (!entryExists) {
                      // Add new entry
                      return { ...student, entries: [...student.entries, ...newEntries] };
                    }

                    // Update or remove existing entry
                    const filteredEntries = student.entries.filter((e) => e.method.id !== methodId);
                    return {
                      ...student,
                      entries: entry ? [...filteredEntries, ...newEntries] : filteredEntries,
                    };
                  },
                ),
              },
            ],
          };
        },
      ),
  ).assessments;
}

export const useAssessmentEntryUpdates = (
  updateState: (
    updater: (current: AssessmentWithEntries[] | undefined) => AssessmentWithEntries[] | undefined,
  ) => void,
) => {
  const updateEntry = (params: UpdateAssessmentEntryParams) => {
    updateState((current) => updateAssessmentEntry(current, params));
  };

  return { updateEntry };
};

type IsSameMarkbookAssessmentEntryProps = {
  assessmentId: string;
  groupId: string;
  methodId: string;
  entry: MarkbookAssessmentEntry;
};

const isSameMarkbookAssessmentEntry = ({
  assessmentId,
  groupId,
  methodId,
  entry,
}: IsSameMarkbookAssessmentEntryProps) => {
  return (
    assessmentId === entry.assessment_id &&
    groupId === entry.group.id &&
    methodId === entry.method.id
  );
};

export function updateMarkbookForGroupsAssessmentEntry(
  {
    categories,
  }: {
    categories: MarkbookCategoryWithStudents[] | undefined;
  },
  {
    studentId,
    assessmentId,
    groupId,
    methodId,
    entry,
    comments,
    methodType,
    categoryId,
  }: UpdateMarkbookForGroupsAssessmentEntryParams,
): MarkbookCategoryWithStudents[] | undefined {
  if (!categories) return categories;

  const isTargetEntry = (e: MarkbookAssessmentEntry) =>
    isSameMarkbookAssessmentEntry({ assessmentId, groupId, methodId, entry: e });

  return updateAtPath(
    { categories },
    'categories',
    (categories: MarkbookCategoryWithStudents[]) => {
      return updateArrayItem(
        categories,
        (category) => category.category.id === categoryId,
        (category) => {
          return {
            ...category,
            students: updateArrayItem(
              category.students,
              (student) => student.student.relation_id === studentId,
              (student) => {
                switch (methodType) {
                  case AssessmentMethodType.Score:
                  case AssessmentMethodType.Grade: {
                    const updatedEntry = entry ?? { score: null, select_list_option_id: null };
                    const updatedEntries = updateArrayItem(
                      student.assessment_entries,
                      isTargetEntry,
                      (e) => ({ ...e, ...updatedEntry }),
                    );

                    return {
                      ...student,
                      assessment_entries: updatedEntries,
                    };
                  }
                  case AssessmentMethodType.Comment: {
                    const existingEntry = student.assessment_entries.find(isTargetEntry);
                    if (!existingEntry) return student;

                    const filteredEntries = student.assessment_entries.filter(
                      (e) => !isTargetEntry(e),
                    );

                    const updatedEntries: MarkbookAssessmentEntry[] = comments?.length
                      ? comments.map((c) => ({ ...existingEntry, ...c }))
                      : [{ ...existingEntry, comment: null, creator: null }];
                    return {
                      ...student,
                      assessment_entries: [...filteredEntries, ...updatedEntries],
                    };
                  }
                }
              },
            ),
          };
        },
      );
    },
  ).categories;
}

export const useMarkbookForGroupsEntryUpdates = (
  updateState: (
    updater: (
      current: MarkbookCategoryWithStudents[] | undefined,
    ) => MarkbookCategoryWithStudents[] | undefined,
  ) => void,
) => {
  const updateEntry = (params: UpdateMarkbookForGroupsAssessmentEntryParams) => {
    updateState((current) =>
      updateMarkbookForGroupsAssessmentEntry({ categories: current }, params),
    );
  };

  return { updateEntry };
};

export const isMarkbookForGroupEntryCellEmpty = (
  studentId: string,
  method: AssessmentMethod,
  assessmentEntries?: AssessmentWithEntries,
): boolean => {
  return !hasMarkbookForGroupAssessmentEntry(studentId, method, assessmentEntries);
};

export const assessmentMethodTypes = {
  isScore: (methodType: AssessmentMethodType) => methodType === AssessmentMethodType.Score,
  isGrade: (methodType: AssessmentMethodType) => methodType === AssessmentMethodType.Grade,
  isComment: (methodType: AssessmentMethodType) => methodType === AssessmentMethodType.Comment,
  isScoreOrGrade: (methodType: AssessmentMethodType) =>
    methodType === AssessmentMethodType.Score || methodType === AssessmentMethodType.Grade,
};

export const getAssessmentId = (methodId: string, assessments: AssessmentWithEntries[]): string => {
  for (const assessment of assessments) {
    if (assessment.methods.some((method) => method.id === methodId)) {
      return assessment.id;
    }
  }
  return '';
};

const getStudentAssessmentEntries = (
  studentId: string | undefined,
  assessments: AssessmentWithEntries[],
): AssessmentWithEntries | undefined => {
  if (!studentId || !assessments) return undefined;

  return assessments.find((assessment) =>
    assessment.groups[0]?.students.some((s) => s.relation_id === studentId),
  );
};

export const transformMarkbookForGroupData = (
  students: GroupMember[],
  assessments?: AssessmentWithEntries[],
): MarkbookForGroupRow[] => {
  return students.map((student) => ({
    id: student.user?.relation_id || '',
    student,
    assessmentEntries: getStudentAssessmentEntries(student.user?.relation_id, assessments ?? []),
  }));
};

type GetMarkbookForGroupsRowsProps = {
  categories: MarkbookCategoryWithStudents[];
  hasNextPage: (id: string) => boolean;
  isFetchingNextPage: (id: string) => boolean;
};

export const getMarkbookForGroupsRows = ({
  categories,
  hasNextPage,
  isFetchingNextPage,
}: GetMarkbookForGroupsRowsProps): MarkbookForGroupsRow[] => {
  return categories.reduce<MarkbookForGroupsRow[]>((acc, { category, students, totalStudents }) => {
    const studentRows: MarkbookForGroupsRow[] = students.map(
      ({ student, assessment_entries, subject_average_score }) => ({
        id: `${category.id}-${student.relation_id}`,
        type: MarkbookForGroupsRowType.Student,
        data: {
          student,
          assessmentEntries: getMarkbookForGroupsAssessmentEntriesMap(assessment_entries),
          categoryId: category.id,
          average: subject_average_score || [],
        },
      }),
    );

    const categoryRow: MarkbookForGroupsCategoryRow = {
      id: category.id,
      type: MarkbookForGroupsRowType.Category,
      data: {
        category,
        totalStudents,
        rowsCount: studentRows.length + (hasNextPage(category.id) ? 1 : 0),
      },
    };

    const loadMoreRow: MarkbookForGroupsLoadMoreRow = {
      id: `${category.id}-load-more`,
      type: MarkbookForGroupsRowType.LoadMore,
      data: { categoryId: category.id },
    };

    const fetchingNextPage = isFetchingNextPage(category.id);
    const hasLoadMoreRow = !fetchingNextPage && hasNextPage(category.id);

    const loadingNextPageRows: MarkbookForGroupsLoadingNextPageRow[] = Array.from({
      length: Math.min(MARKBOOK_PAGE_SIZE, totalStudents - students.length),
    }).map((_, index) => ({
      id: `${category.id}-loading-next-page-${index}`,
      type: MarkbookForGroupsRowType.LoadingNextPage,
      data: { categoryId: category.id },
    }));

    return [
      ...acc,
      categoryRow,
      ...studentRows,
      ...(hasLoadMoreRow ? [loadMoreRow] : []),
      ...(fetchingNextPage ? loadingNextPageRows : []),
    ];
  }, []);
};

type GetMarkbookForGroupsAssessmentEntriesProps = {
  assessmentId: string;
  groupIds: string[];
  methodId: string;
  assessmentEntries: MarkbookAssessmentEntriesMap;
};

export function getMarkbookForGroupsAssessmentEntries({
  assessmentId,
  groupIds,
  methodId,
  assessmentEntries,
}: GetMarkbookForGroupsAssessmentEntriesProps) {
  const entries: MarkbookAssessmentEntry[] = [];

  for (const groupId of groupIds) {
    const groupEntries = assessmentEntries[assessmentId]?.[groupId]?.[methodId] ?? [];
    entries.push(...groupEntries);
  }

  return entries;
}

export const isMarkbookForGroupsEntryCellEmpty = (
  props: Parameters<typeof getMarkbookForGroupsAssessmentEntries>[0],
): boolean => {
  return !getMarkbookForGroupsAssessmentEntries(props).length;
};

const getMarkbookForGroupsAssessmentEntriesMap = (entries: MarkbookAssessmentEntry[]) => {
  return entries.reduce<MarkbookAssessmentEntriesMap>((acc, entry) => {
    const {
      assessment_id,
      group: { id: groupId },
      method: { id: methodId },
    } = entry;

    acc[assessment_id] = acc[assessment_id] ?? {};
    acc[assessment_id][groupId] = acc[assessment_id][groupId] ?? {};
    acc[assessment_id][groupId][methodId] = acc[assessment_id][groupId][methodId] ?? [];

    acc[assessment_id][groupId][methodId].push(entry);
    return acc;
  }, {});
};
